/**
 * Global Image Preloader Utility
 * Preloads images from all pages when the app loads for instant display
 */

// Import all images from About Vardaan page
import aboutBackground from '../assets/images/About Vardaan/About vardaan background.avif';
import sriniImage from '../assets/images/About Vardaan/srini.avif';
import rinoImage from '../assets/images/About Vardaan/rino.jpg';
import gopaImage from '../assets/images/About Vardaan/Gopa Sir.webp';
import premImage from '../assets/images/About Vardaan/prem.avif';
import susheelImage from '../assets/images/About Vardaan/Susheel.png';
import adarshImage from '../assets/images/About Vardaan/Adarsh.jpg';
import mohamedImage from '../assets/images/About Vardaan/Mohamed Zaghloul.jpg';
import hossamImage from '../assets/images/About Vardaan/Hossam Khater.png';
import thanarajahImage from '../assets/images/About Vardaan/P. Thanarajah.jpg';
import ahmadFuadImage from '../assets/images/About Vardaan/Ahmad Fuad Kamarazaman.jpg';
import ahmedElmaghrabyImage from '../assets/images/About Vardaan/ahmed elmaghraby.webp';

// Import all images from Products page
import grcImage from '../assets/Products/GRC/grc 1.webp';
import lapsecImage from '../assets/Products/Lapsec/lapsec 2.webp';
import truckImage from '../assets/Products/Smart Trucks/truck 1.jpg';
import cyberImage from '../assets/Products/Cyber/cyber.webp';
import roboticsImage from '../assets/Products/Robotics/robotics 1.webp';
import auditImage from '../assets/Products/ProSync/audit.webp';

// Import all images from Services page
import dataServicesHero from '../assets/images/Data Services/development.avif';
import aiConsultingHero from '../assets/images/AI Consulting services/AI Consulting Hero.avif';
import erpServicesHero from '../assets/images/ERP Services/Oracle ERP Services.avif';
import technoFinancialHero from '../assets/images/Techno Financial Services/Business-and-Plant-Valuations.avif';
import cyberSecurityHero from '../assets/images/Cyber Security/breach-simulation.avif';
import trainingHero from '../assets/images/Training/Training.webp';

/**
 * Preloads a single image with high priority
 */
const preloadImage = (imageSrc, priority = 'high') => {
  if (!imageSrc) return Promise.resolve();
  
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    // Set fetch priority for faster loading
    if (priority === 'high' && 'fetchPriority' in img) {
      img.fetchPriority = 'high';
    }
    
    img.onload = () => {
      resolve(img);
    };
    
    img.onerror = (error) => {
      // Silently fail - don't block other images
      reject(error);
    };
    
    // Start loading immediately
    img.src = imageSrc;
    
    // Use decode API if available for better performance
    if (img.decode) {
      img.decode().catch(() => {
        // Ignore decode errors
      });
    }
  });
};

/**
 * Preloads all images from all pages
 * This runs once when the app loads to ensure images are ready
 */
export const preloadAllImages = () => {
  // Collect all images from all pages
  const allImages = [
    // About Vardaan images
    aboutBackground,
    sriniImage,
    rinoImage,
    gopaImage,
    premImage,
    susheelImage,
    adarshImage,
    mohamedImage,
    hossamImage,
    thanarajahImage,
    ahmadFuadImage,
    ahmedElmaghrabyImage,
    
    // Products images
    grcImage,
    lapsecImage,
    truckImage,
    cyberImage,
    roboticsImage,
    auditImage,
    
    // Services images
    dataServicesHero,
    aiConsultingHero,
    erpServicesHero,
    technoFinancialHero,
    cyberSecurityHero,
    trainingHero,
  ];
  
  // Preload all images in parallel with high priority
  // Use requestIdleCallback if available to not block main thread
  const preloadImages = () => {
    allImages.forEach((imageSrc, index) => {
      // Stagger slightly to avoid overwhelming the browser
      setTimeout(() => {
        preloadImage(imageSrc, 'high').catch(() => {
          // Silently handle failures
        });
      }, index * 10); // 10ms delay between each image
    });
  };
  
  // Start preloading immediately
  if (typeof requestIdleCallback !== 'undefined') {
    // Use idle time if available
    requestIdleCallback(preloadImages, { timeout: 2000 });
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(preloadImages, 100);
  }
};

/**
 * Preloads images for a specific page
 */
export const preloadPageImages = (pageName) => {
  let images = [];
  
  switch (pageName) {
    case 'about':
      images = [
        aboutBackground,
        sriniImage,
        rinoImage,
        gopaImage,
        premImage,
        susheelImage,
        adarshImage,
        mohamedImage,
        hossamImage,
        thanarajahImage,
        ahmadFuadImage,
        ahmedElmaghrabyImage,
      ];
      break;
    case 'products':
      images = [
        grcImage,
        lapsecImage,
        truckImage,
        cyberImage,
        roboticsImage,
        auditImage,
      ];
      break;
    case 'services':
      images = [
        dataServicesHero,
        aiConsultingHero,
        erpServicesHero,
        technoFinancialHero,
        cyberSecurityHero,
        trainingHero,
      ];
      break;
    default:
      return;
  }
  
  // Preload with highest priority
  images.forEach(imageSrc => {
    preloadImage(imageSrc, 'high').catch(() => {});
  });
};

export default {
  preloadAllImages,
  preloadPageImages,
  preloadImage,
};

